import * as React from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {withRouter, RouteComponentProps} from 'react-router-dom';
import {AssetGroup, User} from '@deviceinsight/ng-ui-api-client';
import {messageBoxSuccess} from '@deviceinsight/ng-ui-components';
import {Button, ButtonRow} from '@deviceinsight/ng-ui-basic-components';
import {UiCondition, UiRule, UiAction} from '../../modules/ruleengine/types';
import RuleNameAndActiveEdit from './RuleNameAndActiveEdit';
import RuleConditionsContainer from './condition/RuleConditionsContainer';
import RuleActionsContainer from './action/RuleActionsContainer';
import {countConditions, validateRule, shouldShowTriggerDetailLink} from './ruleEditHelpers';
import {loadRule, saveUiRule} from '../../api/ruleApi';
import InfoText from '../../components/common/InfoText';
import './RuleEdit.less';
import {
	AUTHORITY_READ_NOTIFICATION_TEMPLATE,
	AUTHORITY_UPDATE_RULE,
	MAX_CONDITIONS,
	MAX_TRIGGER_CONDITIONS
} from '../../modules/ruleengine/config/consts';
import TriggerDetails from './trigger/TriggerDetails';
import {OwnSubtenant} from '../../modules/subtenants/types';
import {getOwnSubtenant} from '../../api/subtenantsApi';
import RuleApiIntegrationContainer from './apiIntegration/RuleApiIntegrationContainer';
import {createAction} from './action/ruleActionHelpers';
import {ACTION_TYPE_WEBSOCKET} from '../../modules/ruleengine/config/consts';

type Props = RouteComponentProps<{ruleId: string}> & {
	assetGroup: AssetGroup;
	currentUser: User;
};

type State = {
	rule: UiRule | undefined;
	savedRule: UiRule | undefined;
	isConditionAddable: boolean;
	isTriggerAddable: boolean;
	canEdit: boolean;
	canReadNotificationTemplates: boolean;
	isTriggerDetailsOpen: boolean;
	ownSubtenant: OwnSubtenant | undefined;
};

const HeaderTooltipTrigger = () => (
	<InfoText
		bubble={{
			id: 'ruleengine.edit.rule.triggerHelp',
			defaultValue: 'Select which datapoint changes should trigger an action'
		}}>
		&nbsp;
	</InfoText>
);

const HeaderTooltipConditions = () => (
	<InfoText
		bubble={{
			id: 'ruleengine.edit.rule.conditionsHelp',
			defaultValue: 'Optionally add a condition when a trigger should result in an action'
		}}>
		&nbsp;
	</InfoText>
);

const HeaderTooltipActions = () => (
	<InfoText
		bubble={{
			id: 'ruleengine.edit.rule.actionsHelp',
			defaultValue: 'Select actions to execute when triggered'
		}}>
		&nbsp;
	</InfoText>
);

class RuleEdit extends React.PureComponent<Props, State> {
	state: State = {
		rule: undefined,
		savedRule: undefined,
		isConditionAddable: true,
		isTriggerAddable: true,
		canEdit: false,
		canReadNotificationTemplates: false,
		isTriggerDetailsOpen: false,
		ownSubtenant: undefined
	};

	componentDidMount() {
		// check the routing param (rule id) and then load rule in case there is one, otherwise create new rule object
		const {
			match: {
				params: {ruleId}
			},
			currentUser,
			assetGroup: {id: assetGroupId}
		} = this.props;
		const canEdit = !!currentUser.authorities.find((authority: string) => authority === AUTHORITY_UPDATE_RULE);
		const canReadNotificationTemplates = !!currentUser.authorities.find(
			(authority: string) => authority === AUTHORITY_READ_NOTIFICATION_TEMPLATE
		);

		getOwnSubtenant()
			.then((ownSubtenant) => {
				this.setState({
					ownSubtenant: ownSubtenant
				});
			})
			.catch((error) => {
				console.error('Subtenant of logged in user could not be loaded: ', error);
			});

		if (ruleId && ruleId !== 'create') {
			loadRule(ruleId, assetGroupId).then((rule) => {
				if (rule) {
					const isTriggerAddable = countConditions(rule.triggerConditions) < MAX_TRIGGER_CONDITIONS;
					const isConditionAddable = countConditions(rule.conditions) < MAX_CONDITIONS;
					this.setState({
						rule,
						savedRule: rule,
						canEdit,
						isConditionAddable,
						isTriggerAddable,
						canReadNotificationTemplates
					});
				}
			});
		} else {
			const {
				assetGroup: {id: assetGroupId}
			} = this.props;
			// $FlowIgnore id is undefined
			this.setState({
				rule: {
					id: undefined,
					name: '',
					active: true,
					assetGroupId: assetGroupId,
					triggerConditions: undefined,
					conditions: undefined,
					actions: [],
					metadata: {
						uuid: '-',
						isValid: false
					},
					deactivatedBySystem: false,
					apiTriggerEnabled: false
				},
				canEdit,
				canReadNotificationTemplates
			});
		}
	}

	validateAndUpdate = (rule: UiRule | null | undefined): void => {
		if (rule) {
			const isValid = validateRule(rule);
			const numConditions = countConditions(rule.conditions);
			const isConditionAddable = numConditions < MAX_CONDITIONS;
			const numTriggers = countConditions(rule.triggerConditions);
			const isTriggerAddable = numTriggers < MAX_TRIGGER_CONDITIONS;
			this.setState({
				rule: {...rule, metadata: {...rule.metadata, isValid}},
				isConditionAddable,
				isTriggerAddable
			});
		}
	};
	onRuleNameChange = (name: string) => {
		const {rule} = this.state;

		if (rule) {
			this.validateAndUpdate({...rule, name});
		}
	};
	onRuleActiveChange = (active: boolean) => {
		const {rule} = this.state;

		if (rule) {
			this.validateAndUpdate({...rule, active});
		}
	};
	onConditionsChange = (conditions: UiCondition | null | undefined) => {
		const {rule} = this.state;

		if (rule) {
			this.validateAndUpdate({...rule, conditions});
		}
	};
	onTriggersChange = (triggerConditions: UiCondition | null | undefined) => {
		const {rule} = this.state;

		if (rule) {
			this.validateAndUpdate({...rule, triggerConditions});
		}
	};
	onActionsChange = (actions: Array<UiAction>) => {
		const {rule} = this.state;

		if (rule) {
			this.validateAndUpdate({...rule, actions});
		}
	};
	onOpenTriggerDetails = () => {
		this.setState({
			isTriggerDetailsOpen: true
		});
	};
	onCloseTriggerDetails = () => {
		this.setState({
			isTriggerDetailsOpen: false
		});
	};
	cancel = () => {
		this.props.history.goBack();
	};
	save = () => {
		const {rule, savedRule, canEdit} = this.state;

		if (rule && canEdit) {
			const {
				metadata: {isValid}
			} = rule;

			if (isValid) {
				saveUiRule(rule, savedRule).then((savedRule) => {
					this.setState(
						{
							rule: savedRule ?? undefined,
							savedRule: savedRule ?? undefined
						},
						() => {
							messageBoxSuccess(
								<Trans i18nKey="ruleengine.save.success.message">The rule has been saved</Trans>,
								{
									autoClose: 4000
								}
							);
						}
					);
				});
			}
		}
	};

	onRuleApiTriggerChange = (enabled: boolean) => {
		const {rule} = this.state;
		if (rule) {
			this.validateAndUpdate({...rule, apiTriggerEnabled: enabled});
		}
	};

	onRuleWebsocketActionChange = (enabled: boolean) => {
		const {rule} = this.state;
		if (rule) {
			if (enabled) {
				// create websocket action
				const actionToAdd = createAction(ACTION_TYPE_WEBSOCKET);
				actionToAdd.metadata.isValid = true;
				if (actionToAdd) {
					const updatedActions = [...rule.actions, actionToAdd];
					this.validateAndUpdate({...rule, actions: updatedActions});
				}
			} else {
				// delete websocket action
				let updatedActions = rule.actions.map((action) => {
					if (action.actionType !== ACTION_TYPE_WEBSOCKET) {
						return action;
					}
				});
				updatedActions = updatedActions.filter(function (element) {
					return element !== undefined;
				});
				this.validateAndUpdate({...rule, actions: updatedActions as UiAction[]});
			}
		}
	};

	render() {
		const {
			rule,
			savedRule,
			isConditionAddable,
			isTriggerAddable,
			canEdit,
			canReadNotificationTemplates,
			isTriggerDetailsOpen,
			ownSubtenant
		} = this.state;
		const {assetGroup} = this.props;
		const ruleIsSynchronised = savedRule && shouldShowTriggerDetailLink(rule, savedRule);

		if (rule) {
			const {
				active,
				triggerConditions,
				conditions,
				actions,
				name,
				metadata: {isValid}
			} = rule;
			return (
				<div className="di form rule-edit-container">
					<RuleNameAndActiveEdit
						name={name}
						active={active}
						onNameChange={this.onRuleNameChange}
						onActiveChange={this.onRuleActiveChange}
						canEdit={canEdit}
						deactivatedBySystem={rule.deactivatedBySystem}
					/>
					<RuleConditionsContainer
						conditions={triggerConditions}
						onChange={this.onTriggersChange}
						onOpenTriggerDetails={ruleIsSynchronised ? this.onOpenTriggerDetails : undefined}
						assetGroup={assetGroup}
						isTrigger={true}
						canEdit={canEdit}
						isAddable={isTriggerAddable}
						addLabel={<Trans i18nKey="ruleengine.edit.rule.addtrigger">Add Trigger</Trans>}
						title={
							<>
								<Trans i18nKey="ruleengine.edit.rule.triggers">Trigger</Trans>
								<HeaderTooltipTrigger />
							</>
						}
					/>
					<RuleConditionsContainer
						conditions={conditions}
						onChange={this.onConditionsChange}
						assetGroup={assetGroup}
						isTrigger={false}
						canEdit={canEdit}
						isAddable={isConditionAddable}
						addLabel={<Trans i18nKey="ruleengine.edit.rule.addcondition">Add Condition</Trans>}
						title={
							<>
								<Trans i18nKey="ruleengine.edit.rule.conditions">Conditions</Trans>
								<HeaderTooltipConditions />
							</>
						}
					/>
					<RuleActionsContainer
						actions={actions}
						onChange={this.onActionsChange}
						canEdit={canEdit}
						canReadNotificationTemplates={canReadNotificationTemplates}
						assetGroup={assetGroup}
						title={
							<>
								<Trans i18nKey="ruleengine.edit.rule.actions">Actions</Trans>
								<HeaderTooltipActions />
							</>
						}
					/>
					<RuleApiIntegrationContainer
						apiTriggerOnChange={this.onRuleApiTriggerChange}
						websocketActionOnChange={this.onRuleWebsocketActionChange}
						ownSubtenant={ownSubtenant}
						rule={rule}
						title={
							<>
								<Trans i18nKey="ruleengine.edit.rule.apiIntegration.title">API Integration</Trans>
							</>
						}
					/>
					<ButtonRow dividing>
						{canEdit && (
							<Button primary disabled={!isValid} onClick={this.save}>
								<Trans i18nKey="general.actions.save">Save</Trans>
							</Button>
						)}
						<Button onClick={this.cancel}>
							<Trans i18nKey="general.actions.cancel">Cancel</Trans>
						</Button>
					</ButtonRow>
					{rule && rule.id && isTriggerDetailsOpen && (
						<TriggerDetails
							rule={rule as {id: string; name: string}}
							assetGroupId={rule.assetGroupId}
							onClose={this.onCloseTriggerDetails}
						/>
					)}
				</div>
			);
		} else {
			return <div>loading rule....</div>;
		}
	}
}

export default withRouter(RuleEdit);
